import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ScanBarcode, Printer, Plus, Trash2, Download, ChevronDown,
  AlertTriangle, Check, Info, Save, FolderOpen, CheckCircle, GripVertical
} from 'lucide-react';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import { SCANNER_COMMANDS, CATEGORIES, buildBarcodeContent, buildBarcodeContentRaw, toHex } from '../utils/scannerCommands';
import { profilesAPI } from '../utils/api';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

function BarcodeImage({ content, label, sublabel, format = 'CODE128' }) {
  const svgRef = useRef(null);
  const canvasRef = useRef(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);

  useEffect(() => {
    if (!content) return;

    if (format === 'QR') {
      // Convert string content to byte array for binary QR
      const bytes = [];
      for (let i = 0; i < content.length; i++) {
        bytes.push(content.charCodeAt(i));
      }
      QRCode.toDataURL([{ data: new Uint8Array(bytes), mode: 'byte' }], {
        errorCorrectionLevel: 'M',
        margin: 2,
        width: 200,
        color: { dark: '#000000', light: '#ffffff' },
      }).then(url => setQrDataUrl(url)).catch(e => console.error('QR generation error:', e));
    } else {
      setQrDataUrl(null);
      if (svgRef.current) {
        try {
          JsBarcode(svgRef.current, content, {
            format,
            width: 2,
            height: 70,
            displayValue: false,
            margin: 10,
            background: '#ffffff',
            lineColor: '#000000',
          });
        } catch (e) {
          console.error('Barcode generation error:', e);
        }
      }
    }
  }, [content, format]);

  return (
    <div className="barcode-container text-center">
      {format === 'QR' && qrDataUrl ? (
        <img src={qrDataUrl} alt="QR Code" className="mx-auto" style={{ width: 200, height: 200 }} />
      ) : (
        <svg ref={svgRef} />
      )}
      <p className="text-sm font-semibold text-gray-800 mt-2 font-mono">{label}</p>
      {sublabel && <p className="text-xs text-gray-500 mt-0.5">{sublabel}</p>}
    </div>
  );
}

export default function BarcodeGeneratorPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isHe = i18n.language === 'he';
  const isAdmin = user?.role === 'admin';
  const [selectedCategory, setSelectedCategory] = useState('communication');
  const [queue, setQueue] = useState([]);
  const [generatedBarcode, setGeneratedBarcode] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showSaveProfile, setShowSaveProfile] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileDesc, setProfileDesc] = useState('');
  const [visibility, setVisibility] = useState('private');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [saveMessage, setSaveMessage] = useState(null);

  // Load users list for admin assignment
  useEffect(() => {
    if (isAdmin) {
      api.get('/profiles/users/list').then(r => setUsersList(r.data)).catch(() => {});
    }
  }, [isAdmin]);

  // Get commands for selected category
  const categoryCommands = Object.entries(SCANNER_COMMANDS).filter(
    ([, cmd]) => cmd.category === selectedCategory
  );

  const handleGenerate = useCallback((key, cmd, selectedValue, selectedFormat) => {
    let data = [];
    let barcodeContent = '';
    let label = '';
    let sublabel = '';
    const format = selectedFormat || 'CODE128';

    if (cmd.type === 'symbology_toggle') {
      // Raw barcode data for enable/disable all
      const opt = cmd.options?.find((o) => o.barcodeData && 
        (selectedValue === cmd.options.indexOf(o)));
      if (!opt) return;
      barcodeContent = buildBarcodeContentRaw(opt.barcodeData);
      label = isHe ? (opt.labelHe || opt.label) : opt.label;
      sublabel = isHe ? cmd.nameHe : cmd.name;
    } else if (cmd.type === 'symbology_enable') {
      // Operand 0x40, param, value
      data = [cmd.param, selectedValue];
      barcodeContent = buildBarcodeContent(cmd.operand, data);
      const opt = cmd.options?.find((o) => o.value === selectedValue);
      label = `${cmd.name}: ${isHe ? (opt?.labelHe || opt?.label) : opt?.label}`;
      sublabel = `Operand 0x40 Param 0x${cmd.param.toString(16).padStart(2,'0').toUpperCase()} → ${selectedValue}`;
    } else if (cmd.type === 'dual_byte') {
      data = selectedValue;
      const preset = cmd.presets?.find((p) => p.value[0] === data[0] && p.value[1] === data[1]);
      label = isHe ? (preset?.labelHe || preset?.label) : preset?.label;
      sublabel = `${isHe ? cmd.nameHe : cmd.name} → [0x${data[0].toString(16).padStart(2,'0')}, 0x${data[1].toString(16).padStart(2,'0')}]`;
      barcodeContent = buildBarcodeContent(cmd.operand, data);
    } else if (cmd.type === 'range') {
      data = [selectedValue];
      label = `${isHe ? cmd.nameHe : cmd.name}: ${selectedValue}`;
      sublabel = `Operand 0x${cmd.operand.toString(16).toUpperCase()} Data 0x${selectedValue.toString(16).padStart(2,'0').toUpperCase()}`;
      barcodeContent = buildBarcodeContent(cmd.operand, data);
    } else if (cmd.type === 'barcode_length') {
      // Use Compound Command (0x5D) to combine Min + Max into a single barcode
      const minVal = selectedValue.min ?? 0;
      const maxVal = selectedValue.max ?? 0;
      if (minVal > maxVal && maxVal !== 0) return; // invalid

      const paramMinHi = (cmd.paramMin >> 8) & 0xFF;
      const paramMinLo = cmd.paramMin & 0xFF;
      const paramMaxHi = (cmd.paramMax >> 8) & 0xFF;
      const paramMaxLo = cmd.paramMax & 0xFF;

      // Compound command format: [subLen, operand, data...] for each sub-command
      // Sub1: Set Min → operand 0x50, data [paramMinHi, paramMinLo, minVal]  → subLen = 4
      // Sub2: Set Max → operand 0x50, data [paramMaxHi, paramMaxLo, maxVal]  → subLen = 4
      const compoundData = [
        4, 0x50, paramMinHi, paramMinLo, minVal,
        4, 0x50, paramMaxHi, paramMaxLo, maxVal,
      ];

      data = compoundData;
      barcodeContent = buildBarcodeContent(0x5D, compoundData);
      const cmdName = isHe ? cmd.nameHe : cmd.name;
      label = `${cmdName}: Min=${minVal}, Max=${maxVal}`;
      sublabel = `Compound 0x5D → Min 0x${cmd.paramMin.toString(16).padStart(4,'0').toUpperCase()} + Max 0x${cmd.paramMax.toString(16).padStart(4,'0').toUpperCase()}`;
    } else if (cmd.rawContent) {
      barcodeContent = cmd.rawContent;
      label = isHe ? cmd.nameHe : cmd.name;
      sublabel = cmd.rawContent;
    } else if (cmd.data) {
      data = cmd.data;
      label = isHe ? cmd.nameHe : cmd.name;
      sublabel = `Operand 0x${cmd.operand.toString(16).toUpperCase()}`;
      barcodeContent = buildBarcodeContent(cmd.operand, data);
    } else {
      data = [selectedValue];
      const opt = cmd.options?.find((o) => o.value === selectedValue);
      label = isHe ? (opt?.labelHe || opt?.label) : opt?.label;
      sublabel = `${isHe ? cmd.nameHe : cmd.name} → 0x${selectedValue.toString(16).padStart(2,'0').toUpperCase()}`;
      barcodeContent = buildBarcodeContent(cmd.operand, data);
    }

    setGeneratedBarcode({
      key,
      content: barcodeContent,
      label,
      sublabel,
      operand: cmd.operand,
      data,
      format,
    });
  }, [isHe]);

  const addToQueue = () => {
    if (generatedBarcode) {
      setQueue((prev) => [...prev, { ...generatedBarcode, id: Date.now() }]);
    }
  };

  const removeFromQueue = (id) => {
    setQueue((prev) => prev.filter((item) => item.id !== id));
  };

  const saveAsProfile = async () => {
    if (!profileName.trim() || queue.length === 0) return;
    setSaveMessage(null);
    try {
      const settings = {};
      queue.forEach((item, index) => {
        const cmdName = item.key || item.label;
        const safeName = cmdName.replace(/\s+/g, '_').toLowerCase();
        settings[safeName] = {
          operand: '0x' + item.operand.toString(16).padStart(2, '0'),
          value: item.data.length === 1 ? item.data[0] : item.data,
          mode: item.label,
          order: index,
        };
      });

      const payload = {
        name: profileName.trim(),
        description: profileDesc.trim() || null,
        settings,
        visibility: isAdmin ? visibility : 'private',
      };
      if (isAdmin && visibility === 'assigned' && selectedUsers.length > 0) {
        payload.assignedUserIds = selectedUsers;
      }

      await profilesAPI.create(payload);

      const visLabel = visibility === 'all' ? (isHe ? 'לכל המשתמשים' : 'all users') :
                       visibility === 'assigned' ? (isHe ? `ל-${selectedUsers.length} משתמשים` : `${selectedUsers.length} users`) : '';
      setSaveMessage({
        type: 'success',
        text: isHe
          ? `הפרופיל נשמר${visLabel ? ` ושויך ${visLabel}` : ''}!`
          : `Profile saved${visLabel ? ` — visible to ${visLabel}` : ''}!`
      });
      setProfileName('');
      setProfileDesc('');
      setVisibility('private');
      setSelectedUsers([]);
      setShowSaveProfile(false);
      setTimeout(() => setSaveMessage(null), 4000);
    } catch (err) {
      setSaveMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save profile' });
    }
  };

  const printQueue = async () => {
    // Pre-render all barcodes
    const renderedItems = [];
    for (const q of queue) {
      if ((q.format || 'CODE128') === 'QR') {
        // Generate QR as data URL
        try {
          const bytes = [];
          for (let i = 0; i < q.content.length; i++) bytes.push(q.content.charCodeAt(i));
          const url = await QRCode.toDataURL([{ data: new Uint8Array(bytes), mode: 'byte' }], {
            errorCorrectionLevel: 'M', margin: 2, width: 200,
          });
          renderedItems.push({ imgHtml: `<img src="${url}" style="width:200px;height:200px;margin:0 auto"/>`, label: q.label, sublabel: q.sublabel });
        } catch (e) {
          console.error('QR render error:', e);
          renderedItems.push({ imgHtml: '<p style="color:red">QR Error</p>', label: q.label, sublabel: q.sublabel });
        }
      } else {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        try {
          JsBarcode(svg, q.content, {
            format: q.format || 'CODE128',
            width: 2, height: 60, displayValue: false, margin: 10,
            background: '#ffffff', lineColor: '#000000',
          });
        } catch (e) { console.error('Barcode render error:', e); }
        renderedItems.push({ imgHtml: svg.outerHTML, label: q.label, sublabel: q.sublabel });
      }
    }

    const barcodesDivs = renderedItems.map(item => `
      <div class="barcode-item">
        ${item.imgHtml}
        <p style="font-weight:bold">${item.label}</p>
        <p style="color:#666">${item.sublabel}</p>
      </div>
    `).join('');

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html><head>
        <title>SophistiScan Config Barcodes</title>
        <style>
          body { font-family: monospace; text-align: center; padding: 20px; }
          .barcode-item { display: block; margin: 25px auto; padding-bottom: 20px; border-bottom: 1px dashed #ccc; page-break-inside: avoid; }
          .barcode-item:last-child { border-bottom: none; }
          .barcode-item p { margin: 5px 0; font-size: 12px; }
          @media print { body { padding: 0; } }
        </style>
      </head><body>
        <h2>SophistiScan SQ400H — Configuration Barcodes</h2>
        <p>Scan each barcode with your scanner to apply settings</p>
        <hr/>
        ${barcodesDivs}
        <script>setTimeout(() => window.print(), 300)<\/script>
      </body></html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)] flex items-center gap-3">
            <ScanBarcode className="w-7 h-7 text-brand-400" />
            {t('barcode.title')}
          </h1>
          <p className="text-gray-500 text-sm mt-1">{t('barcode.scanThis')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Category + Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(CATEGORIES).map(([key, { color }]) => (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all
                  ${selectedCategory === key
                    ? 'text-white shadow-lg'
                    : 'text-gray-400 hover:text-gray-200 bg-[var(--bg-card)] border border-[var(--border)] hover:border-gray-600'
                  }`}
                style={selectedCategory === key ? { background: `${color}30`, border: `1px solid ${color}60`, color } : {}}
              >
                {t(`barcode.categories.${key}`)}
              </button>
            ))}
          </div>

          {/* Command Cards */}
          <div className="space-y-4">
            {categoryCommands.map(([key, cmd]) => (
              <CommandCard
                key={key}
                commandKey={key}
                command={cmd}
                isHe={isHe}
                onGenerate={(val, fmt) => handleGenerate(key, cmd, val, fmt)}
              />
            ))}
          </div>
        </div>

        {/* Right: Preview + Queue */}
        <div className="space-y-6">
          {/* Barcode Preview */}
          <div className="card p-6">
            <h3 style={{color:'var(--text-primary)'}} className="font-semibold mb-4">Preview</h3>
            {generatedBarcode ? (
              <div className="flex flex-col items-center gap-4">
                <BarcodeImage
                  content={generatedBarcode.content}
                  label={generatedBarcode.label}
                  sublabel={generatedBarcode.sublabel}
                  format={generatedBarcode.format || 'CODE128'}
                />
                {generatedBarcode.format && generatedBarcode.format !== 'CODE128' && (
                  <span className="badge-amber text-xs">{generatedBarcode.format}</span>
                )}
                <div className="w-full">
                  <p className="label">HEX Data</p>
                  <div className="hex-display text-xs break-all">
                    {toHex([...Array.from(generatedBarcode.content).map(c => c.charCodeAt(0))])}
                  </div>
                </div>
                <button onClick={addToQueue} className="btn-primary w-full flex items-center justify-center gap-2">
                  <Plus className="w-4 h-4" />
                  {t('barcode.addToQueue')}
                </button>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-600">
                <ScanBarcode className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Select a setting and value to generate a barcode</p>
              </div>
            )}
          </div>

          {/* Queue */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 style={{color:'var(--text-primary)'}} className="font-semibold">
                {t('barcode.queue')} ({queue.length})
              </h3>
              {queue.length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  <button onClick={printQueue} className="btn-ghost text-xs flex items-center gap-1">
                    <Printer className="w-3.5 h-3.5" /> {t('barcode.printAll')}
                  </button>
                  <button onClick={() => setQueue([])} className="btn-ghost text-xs text-red-400 flex items-center gap-1">
                    <Trash2 className="w-3.5 h-3.5" /> {t('barcode.clearQueue')}
                  </button>
                </div>
              )}
            </div>

            {/* Save as Profile - prominent button */}
            {queue.length > 0 && (
              <button
                onClick={() => setShowSaveProfile(true)}
                className="w-full mb-4 px-4 py-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 border-2 border-dashed border-green-500/40 text-green-600 dark:text-green-400 hover:bg-green-500/10 transition-all"
              >
                <Save className="w-4 h-4" />
                {isHe ? 'שמור תור כפרופיל חדש' : 'Save Queue as New Profile'}
              </button>
            )}

            {/* Save message */}
            {saveMessage && (
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-3 text-sm ${
                saveMessage.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-500' : 'bg-red-500/10 border border-red-500/20 text-red-400'
              }`}>
                <CheckCircle className="w-4 h-4" />
                {saveMessage.text}
              </div>
            )}

            {/* Save as Profile Form */}
            {showSaveProfile && queue.length > 0 && (
              <div className="p-4 rounded-lg mb-4 border border-brand-500/30" style={{background:'var(--bg-input)'}}>
                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{color:'var(--text-primary)'}}>
                  <FolderOpen className="w-4 h-4 text-brand-500" />
                  {isHe ? 'שמור תור כפרופיל' : 'Save Queue as Profile'}
                </h4>
                <div className="space-y-3">
                  <input
                    type="text"
                    className="input-field text-sm"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    placeholder={isHe ? 'שם הפרופיל — למשל: סופר רמי לוי' : 'Profile name — e.g. Warehouse Scanner'}
                  />
                  <input
                    type="text"
                    className="input-field text-sm"
                    value={profileDesc}
                    onChange={(e) => setProfileDesc(e.target.value)}
                    placeholder={isHe ? 'תיאור (אופציונלי)' : 'Description (optional)'}
                  />
                  {/* Admin: visibility settings */}
                  {isAdmin && (
                    <div>
                      <label className="label text-xs">{isHe ? 'מי יראה את הפרופיל?' : 'Who can see this profile?'}</label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer border transition-all"
                          style={{
                            borderColor: visibility === 'private' ? 'var(--brand)' : 'var(--border)',
                            background: visibility === 'private' ? 'var(--brand-light)' : 'transparent',
                          }}>
                          <input type="radio" name="vis" value="private" checked={visibility === 'private'}
                            onChange={() => setVisibility('private')} className="accent-brand-500" />
                          <div>
                            <span className="text-sm font-medium" style={{color:'var(--text-primary)'}}>{isHe ? 'רק אני' : 'Only me'}</span>
                            <span className="block text-xs" style={{color:'var(--text-muted)'}}>{isHe ? 'פרופיל פרטי' : 'Private profile'}</span>
                          </div>
                        </label>
                        <label className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer border transition-all"
                          style={{
                            borderColor: visibility === 'all' ? 'var(--brand)' : 'var(--border)',
                            background: visibility === 'all' ? 'var(--brand-light)' : 'transparent',
                          }}>
                          <input type="radio" name="vis" value="all" checked={visibility === 'all'}
                            onChange={() => setVisibility('all')} className="accent-brand-500" />
                          <div>
                            <span className="text-sm font-medium" style={{color:'var(--text-primary)'}}>{isHe ? 'כל המשתמשים' : 'All users'}</span>
                            <span className="block text-xs" style={{color:'var(--text-muted)'}}>{isHe ? 'כולם יראו את הפרופיל' : 'Everyone can see this profile'}</span>
                          </div>
                        </label>
                        <label className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer border transition-all"
                          style={{
                            borderColor: visibility === 'assigned' ? 'var(--brand)' : 'var(--border)',
                            background: visibility === 'assigned' ? 'var(--brand-light)' : 'transparent',
                          }}>
                          <input type="radio" name="vis" value="assigned" checked={visibility === 'assigned'}
                            onChange={() => setVisibility('assigned')} className="accent-brand-500" />
                          <div>
                            <span className="text-sm font-medium" style={{color:'var(--text-primary)'}}>{isHe ? 'משתמשים ספציפיים' : 'Specific users'}</span>
                            <span className="block text-xs" style={{color:'var(--text-muted)'}}>{isHe ? 'בחר למי לשייך' : 'Choose who can see it'}</span>
                          </div>
                        </label>
                      </div>

                      {/* User checkboxes for 'assigned' */}
                      {visibility === 'assigned' && usersList.length > 0 && (
                        <div className="mt-3 max-h-40 overflow-y-auto rounded-lg border p-2 space-y-1" style={{borderColor:'var(--border)', background:'var(--bg-card)'}}>
                          {usersList.filter(u => u.id !== user.id).map(u => (
                            <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-brand-500/5 cursor-pointer">
                              <input type="checkbox" className="accent-brand-500"
                                checked={selectedUsers.includes(u.id)}
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedUsers(prev => [...prev, u.id]);
                                  else setSelectedUsers(prev => prev.filter(id => id !== u.id));
                                }}
                              />
                              <span className="text-sm" style={{color:'var(--text-primary)'}}>{u.displayName}</span>
                              <span className="text-xs" style={{color:'var(--text-muted)'}}>({u.email})</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={saveAsProfile}
                      disabled={!profileName.trim()}
                      className="btn-primary text-xs flex items-center gap-1.5"
                    >
                      <Save className="w-3.5 h-3.5" />
                      {isHe ? 'שמור' : 'Save'}
                    </button>
                    <button onClick={() => setShowSaveProfile(false)} className="btn-secondary text-xs">
                      {isHe ? 'ביטול' : 'Cancel'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {queue.length === 0 ? (
              <p className="text-sm text-center py-4" style={{color:'var(--text-muted)'}}>
                {isHe ? 'התור ריק — הוסף הגדרות מהקטגוריות' : 'Queue is empty — add settings from categories'}
              </p>
            ) : (
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {queue.map((item, index) => (
                  <div
                    key={item.id}
                    draggable
                    onDragStart={() => setDragIndex(index)}
                    onDragOver={(e) => { e.preventDefault(); setDragOverIndex(index); }}
                    onDragEnd={() => {
                      if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
                        setQueue(prev => {
                          const updated = [...prev];
                          const [moved] = updated.splice(dragIndex, 1);
                          updated.splice(dragOverIndex, 0, moved);
                          return updated;
                        });
                      }
                      setDragIndex(null);
                      setDragOverIndex(null);
                    }}
                    onDragLeave={() => setDragOverIndex(null)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg group cursor-grab active:cursor-grabbing transition-all
                      ${dragOverIndex === index ? 'border-2 border-brand-500 border-dashed' : 'border-2 border-transparent'}
                      ${dragIndex === index ? 'opacity-40' : 'opacity-100'}
                    `}
                    style={{ background: 'var(--bg-input)' }}
                  >
                    <GripVertical className="w-4 h-4 shrink-0 text-gray-600 group-hover:text-gray-400" />
                    <span className="text-xs font-mono font-bold shrink-0 w-5 text-center" style={{color:'var(--text-muted)'}}>{index + 1}</span>
                    <Check className="w-4 h-4 text-green-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-300 truncate">{item.label}</p>
                      <p className="text-xs text-gray-600 truncate font-mono">{item.sublabel}</p>
                    </div>
                    <button
                      onClick={() => removeFromQueue(item.id)}
                      className="text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Individual command setting card */
function CommandCard({ commandKey, command, isHe, onGenerate }) {
  const [selectedValue, setSelectedValue] = useState(() => {
    if (command.type === 'symbology_toggle') return 0;
    if (command.type === 'symbology_enable') {
      const def = command.options?.find((o) => o.isDefault);
      return def?.value ?? 1;
    }
    if (command.type === 'dual_byte') {
      return command.defaultValue || command.presets?.[0]?.value || [0, 0];
    }
    if (command.type === 'range') {
      return command.defaultValue ?? command.min;
    }
    if (command.type === 'barcode_length') {
      return { min: command.defaultMin ?? 0, max: command.defaultMax ?? 0 };
    }
    if (command.data) return null;
    const def = command.options?.find((o) => o.isDefault);
    return def?.value ?? command.options?.[0]?.value;
  });

  const [selectedFormat, setSelectedFormat] = useState('CODE128');

  const name = isHe ? (command.nameHe || command.name) : command.name;
  const desc = isHe ? (command.descriptionHe || command.description) : command.description;

  return (
    <div className="card p-5">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <h4 style={{color:'var(--text-primary)'}} className="font-semibold">{name}</h4>
          <p className="text-xs text-gray-600 font-mono mt-0.5">
            Operand: 0x{command.operand.toString(16).padStart(2, '0').toUpperCase()}
          </p>
        </div>
        {command.warning && (
          <span className="badge-red flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Warning
          </span>
        )}
      </div>

      {/* Description */}
      {desc && (
        <p className="text-sm text-gray-500 mb-4 leading-relaxed">{desc}</p>
      )}

      {/* Symbology toggle (Enable All / Disable All) */}
      {command.type === 'symbology_toggle' && (
        <div className="grid grid-cols-2 gap-2 mb-4">
          {command.options.map((opt, idx) => {
            const optLabel = isHe ? (opt.labelHe || opt.label) : opt.label;
            const isSelected = selectedValue === idx;
            return (
              <button
                key={idx}
                onClick={() => setSelectedValue(idx)}
                className={`px-3 py-2 rounded-lg text-sm border transition-all
                  ${isSelected
                    ? 'border-brand-500 bg-brand-500/10 text-brand-300'
                    : 'border-[var(--border)] bg-[var(--bg-input)] text-gray-400 hover:border-gray-600'
                  }`}
              >
                {optLabel}
              </button>
            );
          })}
        </div>
      )}

      {/* Symbology enable/disable */}
      {command.type === 'symbology_enable' && (
        <div className="grid grid-cols-2 gap-2 mb-4">
          {command.options.map((opt) => {
            const optLabel = isHe ? (opt.labelHe || opt.label) : opt.label;
            const isSelected = selectedValue === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setSelectedValue(opt.value)}
                className={`px-3 py-2 rounded-lg text-sm border transition-all
                  ${isSelected
                    ? 'border-brand-500 bg-brand-500/10 text-brand-300'
                    : 'border-[var(--border)] bg-[var(--bg-input)] text-gray-400 hover:border-gray-600'
                  }`}
              >
                {optLabel}
                {opt.isDefault && <span className="text-[10px] text-gray-600 ml-1">(default)</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Standard options */}
      {command.options && !command.type?.startsWith('symbology') && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
          {command.options.map((opt) => {
            const optLabel = isHe ? (opt.labelHe || opt.label) : opt.label;
            const isSelected = selectedValue === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setSelectedValue(opt.value)}
                className={`px-3 py-2 rounded-lg text-sm border transition-all text-left
                  ${isSelected
                    ? 'border-brand-500 bg-brand-500/10 text-brand-300'
                    : 'border-[var(--border)] bg-[var(--bg-input)] text-gray-400 hover:border-gray-600 hover:text-gray-300'
                  }`}
              >
                <span className="block">{optLabel}</span>
                {opt.isDefault && <span className="text-[10px] text-gray-600">default</span>}
                <span className="block text-[10px] font-mono text-gray-600 mt-0.5">
                  0x{opt.value.toString(16).padStart(2, '0').toUpperCase()}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {command.type === 'range' && !command.options && (
        <div className="mb-4">
          <div className="flex items-center gap-4">
            <input
              type="range"
              min={command.min}
              max={command.max}
              value={selectedValue}
              onChange={(e) => setSelectedValue(parseInt(e.target.value))}
              className="flex-1 accent-brand-500"
            />
            <span className="text-lg font-mono text-brand-300 w-16 text-center">
              {command.labels?.[selectedValue] || selectedValue}
            </span>
          </div>
          {command.unit && <p className="text-xs text-gray-600 mt-1">{selectedValue} {command.unit}</p>}
        </div>
      )}

      {command.type === 'dual_byte' && (
        <div className="mb-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {command.presets?.map((preset) => {
              const pLabel = isHe ? (preset.labelHe || preset.label) : preset.label;
              const isSelected = selectedValue[0] === preset.value[0] && selectedValue[1] === preset.value[1];
              return (
                <button
                  key={preset.label}
                  onClick={() => setSelectedValue(preset.value)}
                  className={`px-3 py-2 rounded-lg text-sm border transition-all text-left
                    ${isSelected
                      ? 'border-brand-500 bg-brand-500/10 text-brand-300'
                      : 'border-[var(--border)] bg-[var(--bg-input)] text-gray-400 hover:border-gray-600'
                    }`}
                >
                  {pLabel}
                  <span className="block text-[10px] font-mono text-gray-600 mt-0.5">
                    [{preset.value.map(v => '0x' + v.toString(16).padStart(2,'0')).join(', ')}]
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {command.type === 'barcode_length' && (
        <div className="mb-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label text-xs">{isHe ? 'מינימום' : 'Minimum'}</label>
              <input
                type="number"
                className="input-field font-mono text-center"
                min={command.rangeMin}
                max={command.rangeMax}
                value={selectedValue.min}
                onChange={(e) => setSelectedValue(prev => ({ ...prev, min: Math.max(command.rangeMin, Math.min(command.rangeMax, parseInt(e.target.value) || 0)) }))}
              />
            </div>
            <div>
              <label className="label text-xs">{isHe ? 'מקסימום' : 'Maximum'}</label>
              <input
                type="number"
                className="input-field font-mono text-center"
                min={command.rangeMin}
                max={command.rangeMax}
                value={selectedValue.max}
                onChange={(e) => setSelectedValue(prev => ({ ...prev, max: Math.max(command.rangeMin, Math.min(command.rangeMax, parseInt(e.target.value) || 0)) }))}
              />
            </div>
          </div>
          <p className="text-xs mt-2" style={{color:'var(--text-muted)'}}>
            {isHe
              ? `טווח: ${command.rangeMin}-${command.rangeMax}. הגדר שניהם ל-0 לללא הגבלה.`
              : `Range: ${command.rangeMin}-${command.rangeMax}. Set both to 0 for unlimited.`}
          </p>
          {selectedValue.min > selectedValue.max && selectedValue.max !== 0 && (
            <p className="text-xs mt-1 text-red-400">
              {isHe ? 'שגיאה: מינימום חייב להיות קטן או שווה למקסימום' : 'Error: Minimum must be ≤ Maximum'}
            </p>
          )}
        </div>
      )}

      {command.multiFormat && command.formats && (
        <div className="mb-4">
          <label className="label text-xs">{isHe ? 'פורמט ברקוד' : 'Barcode Format'}</label>
          <div className="flex flex-wrap gap-2">
            {['CODE128', ...command.formats.filter(f => f !== 'CODE128')].map((fmt) => (
              <button
                key={fmt}
                onClick={() => setSelectedFormat(fmt)}
                className={`px-3 py-1.5 rounded-lg text-xs font-mono font-medium border transition-all
                  ${selectedFormat === fmt
                    ? 'border-brand-500 bg-brand-500/10 text-brand-300'
                    : 'border-[var(--border)] bg-[var(--bg-input)] text-gray-400 hover:border-gray-600'
                  }`}
              >
                {fmt}
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={() => onGenerate(command.data ? null : selectedValue, selectedFormat)}
        className="btn-primary text-sm flex items-center gap-2"
        disabled={command.type === 'barcode_length' && selectedValue.min > selectedValue.max && selectedValue.max !== 0}
      >
        <ScanBarcode className="w-4 h-4" />
        {isHe ? 'צור ברקוד' : 'Generate Barcode'}
      </button>
    </div>
  );
}
